import PropTypes from 'prop-types'
import styled from 'styled-components'
import { ErrorMessage, useFormikContext } from 'formik'
import { Wrapper } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import inRange from 'lodash/inRange'

import {
  FloatingWrapper,
  FloatingLabel,
  FloatingInput,
  FloatingHint,
} from 'components/styled/helpers/floating'
import ErrorSmall from 'components/form/error-small'
import { getFieldState, composeIsoDate } from './helpers'

const FIELDS = [
  { suffix: 'Day', maxLength: 2 },
  { suffix: 'Month', maxLength: 2 },
  { suffix: 'Year', maxLength: 4 },
]

const validation = {
  Day: (value) => {
    if (!value) return null

    const isNumber = /^[0-9]*$/.test(value)
    if (!isNumber) return 'Day must be a number.'

    const isValidDay = inRange(parseInt(value), 1, 32)
    if (!isValidDay) return 'Day must be between 1-31.'
  },
  Month: (value) => {
    if (!value) return null

    const isNumber = /^[0-9]*$/.test(value)
    if (!isNumber) return 'Month must be a number.'

    const isValidDay = inRange(parseInt(value), 1, 13)
    if (!isValidDay) return 'Month must be between 1-12.'
  },
  Year: (value) => {
    if (!value) return null

    const isNumber = /^[0-9]{4}$/.test(value)
    if (!isNumber) return 'Year must be a 4 digit number, e.g. 2020.'
  },
}

const StyledLabel = styled.label`
  margin-bottom: ${GTR.XS};
  color: ${COLOR.GREY.DARK};
  font-weight: ${FONT.WEIGHT.MEDIUM};
`

const StyledDateError = styled.div`
  margin: 0 0 ${GTR.XS};
  color: ${COLOR.STATE.ERROR};
  font-size: ${FONT.SIZE.XS};
`

const StyledFieldsWrapper = styled(Wrapper)`
  display: flex;
  margin-bottom: ${GTR.XXS};
`

const StyledFieldWrapper = styled(FloatingWrapper)`
  flex: 1;

  &:not(:last-of-type) {
    margin-right: ${GTR.S};
  }
`

const DateField = ({
  composeParts,
  field: { suffix, maxLength },
  formik,
  handleSave,
  hasDateError,
  name,
}) => {
  const { handleBlur, setFieldValue, values } = formik
  const inputName = name + suffix
  const { hasError, hasHighlight, hasValue } = getFieldState({
    formik,
    name: inputName,
  })

  // saves on blur rather than per keystroke, so typing 28 doesn't save the
  // 2nd on the way
  const handleComposedBlur = () => {
    const parts = {
      day: values[`${name}Day`] ?? '',
      month: values[`${name}Month`] ?? '',
      year: values[`${name}Year`] ?? '',
    }
    const savedValue = values[name] || null

    if (!parts.day && !parts.month && !parts.year) {
      if (savedValue === null) return
      setFieldValue(name, '')
      handleSave && handleSave({ name, value: null })
      return
    }

    const composed = composeIsoDate(parts)
    if (composed && composed !== savedValue) {
      setFieldValue(name, composed)
      handleSave && handleSave({ name, value: composed })
    }
  }

  return (
    <StyledFieldWrapper>
      <FloatingInput
        id={inputName}
        onBlur={(e) => {
          handleBlur(e)
          if (composeParts) {
            handleComposedBlur()
            return
          }
          const value = e.target.value
          handleSave && handleSave({ name: inputName, value })
        }}
        error={hasError || hasDateError}
        highlight={hasHighlight}
        maxLength={maxLength}
        name={inputName}
        validate={validation[suffix]}
      />
      <FloatingLabel
        htmlFor={inputName}
        error={hasError || hasDateError}
        full={hasValue}
      >
        {suffix}
      </FloatingLabel>
    </StyledFieldWrapper>
  )
}

const DateSplit = ({ composeParts, handleSave, hint, label, name }) => {
  const formik = useFormikContext()
  const hasSomeError = FIELDS.some(
    ({ suffix }) => getFieldState({ formik, name: name + suffix }).hasError
  )
  const allHaveValue = FIELDS.every(
    ({ suffix }) => getFieldState({ formik, name: name + suffix }).hasValue
  )
  const hasHighlight = formik.status?.highlightEmpty && !allHaveValue
  const hasDateError = composeParts && getFieldState({ formik, name }).hasError

  return (
    <FloatingWrapper
      error={hasSomeError || hasDateError}
      highlight={hasHighlight}
    >
      <StyledLabel>{label}</StyledLabel>
      {composeParts && (
        <ErrorMessage name={name}>
          {(message) => <StyledDateError>{message}</StyledDateError>}
        </ErrorMessage>
      )}
      <StyledFieldsWrapper>
        {FIELDS.map((field) => (
          <DateField
            composeParts={composeParts}
            name={name}
            formik={formik}
            handleSave={handleSave}
            hasDateError={hasDateError}
            field={field}
            key={field.suffix}
          />
        ))}
      </StyledFieldsWrapper>
      {hint && <FloatingHint>{hint}</FloatingHint>}
      {FIELDS.map((field) => (
        <ErrorSmall name={name + field.suffix} key={field.suffix} />
      ))}
    </FloatingWrapper>
  )
}

DateSplit.propTypes = {
  // saves one composed ISO date under `name` on blur, instead of
  // saving each Day/Month/Year part separately
  composeParts: PropTypes.bool,
  handleSave: PropTypes.func,
  hint: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
}

DateSplit.defaultProps = {
  composeParts: false,
  hint: '',
}

export default DateSplit
